import { changeSubCategoryKey } from "./string_helper";

const normStatus = (s) => {
  const v = (s || "").toLowerCase();
  if (v === "pass") return "pass";
  if (v === "fail") return "fail";
  if (v === "partial") return "partial";
  // Three words, one meaning on the canvas: the step produced no result.
  // "skipped" = an upstream step in this branch failed; "timed out" = the run's
  // end-time cutoff fired; "stopped" = an older run halted the whole suite.
  if (v === "stopped" || v === "skipped" || v === "timed out") return "stopped";
  if (v === "running" || v === "in progress") return "running";
  return "";
};

// Single source of truth for category → visual tone (matches CatBadge intent).
export const categoryTone = (category) => {
  const c = (category || "").toLowerCase();
  if (c === "compare") return "blue";
  if (c.startsWith("ui")) return "violet";
  return "slate"; // process / process automation / anything else
};

// ── Per-subcategory visual identity ──────────────────────────────
// Single source of truth: every subcategory gets a distinct hue, a pale
// tint (card background), an Ant Design icon name, and a short label pill.
// Grouped loosely by category family so related steps still read together.
export const SUBCATEGORY_META = {
  // compare — blue / teal family
  file2spec: {
    label: "File → Spec",
    color: "#0E8A8F",
    tint: "#E6F4F4",
    icon: "snippets",
  },
  file2Db: {
    label: "File → DB",
    color: "#185FA5",
    tint: "#E7F0F8",
    icon: "database",
  },
  file2File: {
    label: "File → File",
    color: "#2D7DD2",
    tint: "#E8F1FB",
    icon: "diff",
  },
  db2Db: {
    label: "DB → DB",
    color: "#3D52A0",
    tint: "#EAEDF6",
    icon: "cluster",
  },
  // process — amber / slate family
  eventMonitor: {
    label: "Event Monitor",
    color: "#B8860B",
    tint: "#F8F1DE",
    icon: "eye",
  },
  extractCompressed: {
    label: "Extract",
    color: "#C2410C",
    tint: "#FBEBE2",
    icon: "filezip",
  },
  fileManipulation: {
    label: "File Edit",
    color: "#8A6D3B",
    tint: "#F3EEE3",
    icon: "tool",
  },
  move: {
    label: "Move / Copy",
    color: "#4B7B7E",
    tint: "#E9F1F1",
    icon: "drag",
  },
  // ui — violet family
  uiTest: {
    label: "UI Test",
    color: "#5B3F9E",
    tint: "#EDE8F6",
    icon: "robot",
  },
};

// Per-category fallback when a subcategory is unknown / missing.
const CATEGORY_FALLBACK = {
  compare: {
    // Display label only — the map key "compare" (data) is unchanged.
    label: "Reconciliation",
    color: "#185FA5",
    tint: "#E7F0F8",
    icon: "swap",
  },
  process: {
    label: "Process",
    color: "#8A6D3B",
    tint: "#F3EEE3",
    icon: "thunderbolt",
  },
  ui: {
    label: "UI Automation",
    color: "#5B3F9E",
    tint: "#EDE8F6",
    icon: "robot",
  },
};

const DEFAULT_META = {
  label: "Step",
  color: "#6C8A9B",
  tint: "#EEF1F3",
  icon: "appstore",
};

export const resolveSubMeta = (category, subCategory) => {
  const raw = (subCategory || "").trim();
  if (SUBCATEGORY_META[raw]) return SUBCATEGORY_META[raw];
  const key = changeSubCategoryKey(raw);
  if (key && SUBCATEGORY_META[key]) return SUBCATEGORY_META[key];
  const c = (category || "").toLowerCase();
  if (c.startsWith("ui")) return CATEGORY_FALLBACK.ui;
  if (c === "process" || c.startsWith("process"))
    return CATEGORY_FALLBACK.process;
  if (c === "compare") return CATEGORY_FALLBACK.compare;
  return DEFAULT_META;
};

/**
 * @param rows  result-detail rows or builder step rows
 * @param kind  "result" (default) | "builder"
 */
export function toFlowNodes(rows, kind = "result") {
  if (!Array.isArray(rows)) return [];

  if (kind === "builder") {
    return [...rows]
      .map((r) => ({
        key: r.key ?? r.sequence ?? r.stepReference,
        seq: r.sequence,
        // ref/parentId drive layoutTree. A step's identity in the tree is its
        // stepReference — the same value the backend stores in parent_step_name.
        ref: r.stepReference,
        parentId: r.parentId ?? null,
        title: r.stepReference || "Untitled step",
        subtitle: r.taskName || r.subCategoryValue || r.subcategory || "",
        category: r.categoryValue || r.category || "",
        subCategory: r.subCategoryValue || r.subcategory || "",
        status: "pending",
        stopIfFailed: !!r.stopIfFailed, // badged on the node card when ON
        raw: r,
      }))
      .sort((a, b) => (a.seq ?? 0) - (b.seq ?? 0));
  }

  return [...rows]
    .map((r) => ({
      key: r.stepResultId || r.stepSequenceNumber,
      seq: r.stepSequenceNumber,
      // Result rows identify a step by its name; parent_step_name points at the
      // step it waited on. Both are null for runs recorded before fan-out existed,
      // which layoutTree reads as a linear chain.
      ref: r.stepName,
      parentId: r.parentStepName ?? null,
      title: r.stepName || "Untitled step",
      subtitle: r.taskName || r.subCategory || "",
      category: r.category || "",
      subCategory: r.subCategory || "",
      // No result yet = the run never reached this step → "skipped" (dimmed on
      // the canvas), not "fail".
      status: normStatus(r.stepTestResult) || "skipped",
      stopIfFailed: !!r.stopIfFailed,
      pass: r.totalPassCount,
      fail: r.totalFailCount,
      raw: r,
    }))
    .sort((a, b) => (a.seq ?? 0) - (b.seq ?? 0));
}
